import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useMemo, useRef, useState } from "react"
import { useForm, useWatch } from "react-hook-form"
import { toast } from "sonner"
import z from "zod"
import useForms from "../hooks/use-form"
import useWidget from "../hooks/use-widget"
import { generateStageSchema } from "../lib/schema-generator"
import { normalizeApiResponse, removeTrailingChars } from "../lib/utils"
import { useFormStore } from "../stores/use-form-store"
import { useLeadStore } from "../stores/use-lead-store"
import type { FormConfig, STAGE } from "../types/form-types"
import { logger } from "../utils/logger"
import { FormActions } from "./actions/form-actions"
import { WidgetActions } from "./actions/widget-actions"
import { Section } from "./section"
import { SuccessPopup } from "./success-popup"
import { Alert, AlertDescription } from "./ui/alert"
import { Form } from "./ui/form"
import { Spinner } from "./ui/spinner"
import { useLogicStore } from "@/stores/use-conditional-logic-store"
import { useShallow } from "zustand/react/shallow"
import type { AxiosError } from "axios"

interface stageProps {
	stage: STAGE
	isPreview: boolean
	isFirstStage: boolean
	isLastStage: boolean
	config: FormConfig
	universityId?: string
	batchId: string
	handlePrevious?: () => void
	handleNext?: () => void
	applicationId: string
	isLeadPunch: boolean
	isReferralWidget: boolean
	source: string
	isRenewal: boolean
}

export const Stage = ({
	stage,
	isPreview = true,
	isFirstStage = true,
	isLastStage = true,
	config,
	universityId,
	batchId,
	handleNext,
	handlePrevious,
	applicationId,
	isLeadPunch,
	isReferralWidget,
	source,
	isRenewal,
}: stageProps) => {
	const [successPopupOpen, setSuccessPopupOpen] = useState(false)
	const [widgetApplicationId, setWidgetApplicationId] = useState<
		string | null
	>(null)
	const { leadId } = useLeadStore()
	const { schema, defaultValues, otpVerificationMap } = useMemo(() => {
		return generateStageSchema(stage)
	}, [stage])

	const {
		setVerifiedFields,
		verifiedFields,
		clearVerifiedFields,
		setSubmissionValues,
		setApplicationSubmission,
		leadDetails,
		// submissionValues,
	} = useFormStore()
	const {
		useSubmitFormStage,
		useGetApplicationSubmission,
		useGetApplicationStageSubmissionById,
	} = useForms(
		window.__DF_MODE || (config?.status === "ACTIVE" ? "PROD" : "TEST"),
	)
	const { useSubmitWidgetStage, useReferralWidgetSubmition } = useWidget(
		window.__DF_MODE || (config?.status === "DRAFT" ? "TEST" : "PROD"),
	)

	const {
		mutateAsync: submitForm,
		isPending: isFormSubmitting,
		isSuccess: isFormSubmitted,
	} = useSubmitFormStage(
		universityId!,
		batchId,
		applicationId,
		stage.stage_id,
		leadId || "",
		config?.form_id || "",
	)
	const { params, metaData } = useLeadStore()
	const {
		mutateAsync: submitWidget,
		isPending: isWidgetSubmitting,
		isSuccess: isWidgetSubmitted,
		isError: isWidgetSubmissionError,
		error = {
			response: {
				data: {
					message: "",
				},
			},
		},
	} = useSubmitWidgetStage(
		universityId!,
		config?.widget_id ?? "",
		stage.stage_id,
		config?.associated_form_id || "",
		widgetApplicationId || "",
		params,
		metaData,
	)

	const {
		mutateAsync: submitReferralWidget,
		isPending: isReferralWidgetSubmitting,
		isSuccess: isReferralWidgetSubmitted,
	} = useReferralWidgetSubmition(
		universityId!,
		config?.widget_id ?? "",
		config?.associated_form_id || "",
		params,
		metaData,
		source,
	)

	const {
		data: stageSubmissionData,
		isLoading: isGetApplicationStageDataLoading,
		isFetching: isGetApplicationStageDataFetching,
	} = useGetApplicationStageSubmissionById(
		universityId!,
		batchId,
		applicationId,
		stage.stage_id,
		config?.form_id || "",
	)

	const {
		data: applicationSubmission,
		isLoading: isGetApplicationSubmissionLoading,
		// isFetching: isGetApplicationSubmissionFetching,
	} = useGetApplicationSubmission(universityId!, batchId, applicationId)

	const submitTypeRef = useRef<
		"apply" | "enquire" | "leadPunch" | "referral"
	>("apply")
	const [isResetTriggered, setIsResetTriggered] = useState(
		config.type === "WIDGET" || !applicationId || applicationId == "",
	)

	const form = useForm<z.infer<typeof schema>>({
		resolver: zodResolver(schema as any),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: defaultValues,
		shouldUnregister: true,
	})

	const values = useWatch({ control: form.control })

	const {
		resetHiddenFields,
		errors,
		setFieldSectionMap,
		setSectionFieldCount,
		resetFieldSectionMap,
		resetSectionFieldCount,
	} = useLogicStore(
		useShallow(state => ({
			resetHiddenFields: state.resetHiddenFields,
			errors: state.errors,
			setFieldSectionMap: state.setFieldSectionMap,
			setSectionFieldCount: state.setSectionFieldCount,
			resetFieldSectionMap: state.resetFieldSectionMap,
			resetSectionFieldCount: state.resetSectionFieldCount,
		})),
	)

	const formHandleSubmit = async (values: z.infer<typeof schema>) => {
		const hasManualErrors = Object.keys(errors).length > 0
		if (hasManualErrors) {
			logger.warning("Form has errors, not submitting")
			return
		}

		for (const [fieldId, isVerified] of verifiedFields.entries()) {
			if (!isVerified) {
				form.setError(fieldId, {
					message: "OTP verification is required",
				})
				return
			}
		}

		logger.info("form submitted values: ", values)
		if (!isPreview) {
			await submitForm(values, {
				onError: () => {
					toast.error("something went wrong")
				},
				onSuccess: () => {
					resetHiddenFields()
					resetSectionFieldCount()
					resetFieldSectionMap()

					if (isLastStage) {
						if (
							!window.__DF_USER__ ||
							(window.__DF_USER__.type !==
								"L1_DOCUMENT_VERIFIER" &&
								window.__DF_USER__.type !==
									"L2_DOCUMENT_VERIFIER")
						) {
							setSuccessPopupOpen(true)
						}
						form.reset()
						setTimeout(() => {
							setSuccessPopupOpen(false)
							window.dispatchEvent(new Event("form-submitted"))
						}, 1000)
					}
				},
			})
		}
		if (!isLastStage && handleNext) {
			setSubmissionValues(values)
			handleNext()
		}
	}

	const WidgetHandleSubmit = async (values: z.infer<typeof schema>) => {
		if (isPreview && submitTypeRef.current !== "leadPunch") return

		if (submitTypeRef.current === "referral") {
			await submitReferralWidget(
				{
					payload: values,
				},
				{
					onError: err => {
						const error = err as AxiosError<{ message: string }>
						if (error.response) {
							if (error.response.status === 400) {
								toast.error("Already registered")
							} else {
								toast.error(error.response.data.message)
							}
						} else {
							toast.error("something went wrong!!")
						}
					},
				},
			)
		} else {
			const { application_id, domain } = await submitWidget({
				payload: values,
				isCreateApplication: submitTypeRef.current === "apply",
				leadId,
			})

			if (application_id) {
				setWidgetApplicationId(application_id)
			}

			const email = values[leadDetails?.email_field_id] || ""
			const phoneNumber = values[leadDetails?.mobile_field_id] || ""

			if (application_id && config && submitTypeRef.current === "apply") {
				const status = config.status === "DRAFT" ? "test/" : ""
				// window.location.href = `${domain}/onboarding/universities/${universityId}/forms/${config.associated_form_id}?application_id=${application_id}`
				window.open(
					`${removeTrailingChars(domain, "/")}/${status}login?email=${email}&phoneNumber=${phoneNumber}`,
					"_blank",
				)
			}
		}

		if (isLastStage) {
			form.reset()
			if (submitTypeRef.current === "leadPunch") {
				window.dispatchEvent(new Event("widget-submitted"))
				return
			}
			setSuccessPopupOpen(true)
			setTimeout(() => {
				window.dispatchEvent(new Event("df-config-updated"))
				setSuccessPopupOpen(false)
			}, 1000)
		}
	}

	useEffect(() => {
		clearVerifiedFields()
		const verifiedFieldsArray = Object.entries(otpVerificationMap).map(
			([fieldId, isVerified]) => ({ fieldId, isVerified }),
		)
		setVerifiedFields(verifiedFieldsArray)
	}, [otpVerificationMap, setVerifiedFields])

	useEffect(() => {
		if (stageSubmissionData && stageSubmissionData.data) {
			const submissionValues = normalizeApiResponse(
				stageSubmissionData.data,
				schema,
			)

			const mergedValues: Record<string, any> = {}
			for (const key in submissionValues) {
				if (submissionValues[key] === "") {
					mergedValues[key] = values[key]
				} else {
					mergedValues[key] = submissionValues[key]
				}
			}
			// const submissionValues = stageSubmissionData.data
			setSubmissionValues(mergedValues)
			if (!isResetTriggered && !isGetApplicationStageDataFetching) {
				form.reset(mergedValues)
				setIsResetTriggered(true)
			}
		}

		if (stageSubmissionData && stageSubmissionData.verifications) {
			const verifiedFields = stageSubmissionData.verifications.map(
				field => {
					return {
						fieldId: field,
						isVerified: true,
					}
				},
			)
			setVerifiedFields(verifiedFields)
		}
	}, [stageSubmissionData, isGetApplicationStageDataFetching])

	const { register, unregister, watch } = form

	useEffect(() => {
		if (!window.RHF) return

		const handler = setTimeout(() => {
			window.values = values
			window.dispatchEvent(new Event("values_updated"))
		}, 150)

		logger.info("[SDK] values: ", values)

		return () => clearTimeout(handler)
	}, [values])

	useEffect(() => {
		window.RHF = {
			unregisterField: (fieldName: string) => {
				unregister(fieldName)
			},
			registerField: (fieldName: string) => {
				register(fieldName)
			},
			setHiddenFields: (fieldName: string, value: boolean) => {
				const {
					hiddenFields,
					setHiddenFields,
					updateSectionFieldCount,
					fieldSectionMap,
				} = useLogicStore.getState()

				if (hiddenFields[fieldName] === value) {
					return
				}

				// if (!value && hiddenFields[fieldName]) {
				// 	form.setValue(fieldName, submissionValues[fieldName])
				// }

				setHiddenFields(fieldName, value)
				if (value) {
					updateSectionFieldCount(fieldSectionMap[fieldName], -1)
				} else if (
					hiddenFields[fieldName] !== undefined &&
					hiddenFields[fieldName]
				) {
					updateSectionFieldCount(fieldSectionMap[fieldName], +1)
				}
			},
			setError: (fieldName: string, error: string) => {
				const { errors, setError } = useLogicStore.getState()

				if (errors[fieldName] && errors[fieldName] === error) return
				setError(fieldName, error)
			},
			clearErrors: (fieldName: string) => {
				const { errors, clearError } = useLogicStore.getState()

				if (!errors[fieldName]) return
				clearError(fieldName)
			},
			get hiddenFields() {
				return useLogicStore.getState().hiddenFields
			},
			get errors() {
				return useLogicStore.getState().errors
			},
			watch: watch,
		}

		window.dispatchEvent(new Event("RHF_READY"))
	}, [])

	useEffect(() => {
		if (applicationSubmission) {
			window.submissionValues = applicationSubmission
			setApplicationSubmission(applicationSubmission)
		}
	}, [applicationSubmission])

	useEffect(() => {
		const fieldSectionMap: Record<string, number> = {}
		// const sectionFieldCount: number[] = []

		stage.sections.forEach((section, sectionIdx) => {
			let fieldCount = 0

			section.rows.forEach(row => {
				row.columns.forEach(column => {
					column.fields.forEach(field => {
						fieldSectionMap[field.field_id] = sectionIdx
						fieldCount += 1
					})
				})
			})

			// sectionFieldCount[sectionIdx] = fieldCount
			setSectionFieldCount(sectionIdx, fieldCount)
		})

		// ✅ Update your store once after computation
		setFieldSectionMap(fieldSectionMap)
	}, [stage, setFieldSectionMap, setSectionFieldCount])

	if (
		!isResetTriggered ||
		isGetApplicationSubmissionLoading ||
		isGetApplicationStageDataLoading
	)
		return <Spinner />
	return (
		<div className="df-stage">
			<SuccessPopup
				isRefferal={isReferralWidget}
				open={successPopupOpen}
				setOpen={setSuccessPopupOpen}
			/>
			<Form
				{...form}
				key={stage.stage_id}
			>
				<form
					onSubmit={form.handleSubmit(
						config?.type === "FORM"
							? formHandleSubmit
							: WidgetHandleSubmit,
					)}
				>
					{stage.sections.map((section, sectionIdx) => {
						return (
							<Section
								section={section}
								key={section.section_id}
								form={form}
								sectionIdx={sectionIdx}
							/>
						)
					})}

					{/* change this toast message in future */}
					{/* {submitError && (
						<Alert
							variant="destructive"
							className="mt-6"
						>
							<AlertDescription>{submitError}</AlertDescription>
						</Alert>
					)} */}

					{isWidgetSubmissionError && error && (
						<Alert
							variant="destructive"
							className="my-6"
						>
							<AlertDescription>
								{"response" in error &&
								error.response?.data?.message
									? error.response.data.message
									: error instanceof Error
										? error.message
										: ""}
							</AlertDescription>
						</Alert>
					)}

					{!isRenewal &&
						(config?.type === "FORM" ? (
							<FormActions
								isPreview={isPreview}
								isFirstStage={isFirstStage}
								isLastStage={isLastStage}
								handlePrevious={handlePrevious}
								handleNext={handleNext}
								isSubmitted={isFormSubmitted}
								isSubmitting={isFormSubmitting}
								isDisabled={
									isGetApplicationStageDataLoading ||
									isGetApplicationSubmissionLoading
								}
							/>
						) : (
							<WidgetActions
								isPreview={isPreview}
								isFirstStage={isFirstStage}
								isLastStage={isLastStage}
								handlePrevious={handlePrevious}
								handleNext={handleNext}
								isSubmitted={
									isReferralWidget
										? isReferralWidgetSubmitted
										: isWidgetSubmitted
								}
								isSubmitting={
									isReferralWidget
										? isReferralWidgetSubmitting
										: isWidgetSubmitting
								}
								submitTypeRef={submitTypeRef}
								isLeadPunch={isLeadPunch}
								isReferralWidget={isReferralWidget}
								isDisabled={
									isGetApplicationStageDataLoading ||
									isGetApplicationSubmissionLoading
								}
							/>
						))}
				</form>
			</Form>
		</div>
	)
}
